/* eslint-disable no-bitwise */

import { createSelector } from '@reduxjs/toolkit';
import moment from 'moment';
import { selectFavouriteChats, selectMarkUnreadChats, selectMutedChats } from '../appSlice';
import {
    isValidContactEntity,
    selectAllUsers,
    selectAuthLoading,
    selectAuthUserByUuid,
    selectHuntGroupDict,
    selectIsAus,
    selectMailboxFromExt,
    selectPhonebookEntityInArr,
    selectShortCodes,
    selectUserEntityByExtension,
    selectUsersDictionary,
} from '../authSlice';
import { selectAllCallerIds, selectParkingSlots, selectTenantClients } from '../callsSlice';
import { selectAllChatEntries, selectChatsOnly } from '../chatSlice';
import type { CallWhenAvailableUser } from '../statusSlice';
import { selectUserGlobalDnd, selectUserStatuses } from '../statusSlice';
import {
    selectCurrentUser,
    selectCurrentUserSipName,
    selectCurrentUserSipPassword,
    selectPhoneSettingByKey,
    selectUserCountryCode,
    selectUserSipPermissions,
} from '../userSlice';
import { IChat, IMailboxMenu, IParkingSlot, IShortcode, IThread, IUser, IUserStatus, PhonebookContactEntity, SipPermissionFeature, UserEntity } from '../../../types';
import { RootState } from '../../store';
import { SIPLogin } from '../../middleware/sipMiddleware';
import { getNumbersArr } from '../../../pages/phone/call-history/CallHistoryRow/ExternalCallHistoryRow';

import { nebulaChatEntitySelectors, NebulaChatState, selectExtraChatters, selectNebulaChats } from '../chatNSlice';
import { Chatter, Conversation } from '../../services/nebulaChat';
import { sanitizeStringForRegex } from '../../../helpers';

type CallingPermissionBase<T> = Record<SipPermissionFeature, T>;

type UserPermissionDir = CallingPermissionBase<Set<string>>;
type CallingUserPermission = CallingPermissionBase<boolean>;
type CallShortCodes = CallingPermissionBase<string | undefined>;
type UserHasPermission = CallingPermissionBase<boolean>;

const SIP_PERMISSIONS = ['pickup', 'whisper', 'listen', 'barge'];

const selectUserPermissionsDir = createSelector(
    [selectUserSipPermissions, selectHuntGroupDict],
    (permissions, huntGroupDict): UserPermissionDir => {
        const permissionsDir: UserPermissionDir = {
            listen: new Set(),
            whisper: new Set(),
            barge: new Set(),
            pickup: new Set()
        };

        for (let i = 0; i < permissions.length; i += 1) {
            const p = permissions[i];
            if (!p.feature) {
                continue;
            }

            if (!SIP_PERMISSIONS.some(c => c === p.feature)) {
                console.error('Invalid permissions feature', p.feature);
                continue;
            }

            let users: string[];

            if (p.user_uuid) {
                users = [p.user_uuid];
            } else if (p.group_uuid) {
                const group = huntGroupDict[p.group_uuid];

                if (!group || !group.members || group.members.length < 1) {
                    continue;
                }

                users = group.members;
            } else {
                console.error('No to data in permission', p);
                continue;
            }

            users.forEach(u => {
                switch (p.feature) {
                    case 'pickup':
                        permissionsDir.pickup.add(u);
                        break;
                    case 'whisper':
                        permissionsDir.whisper.add(u);
                        break;
                    case 'barge':
                        permissionsDir.barge.add(u);
                        break;
                    case 'listen':
                        permissionsDir.listen.add(u);
                        break;
                    default:
                }
            });
        }

        return permissionsDir;
    }
);

const selectCallingPermissionOnUser = createSelector(
    [selectUserPermissionsDir, (_: RootState, uuid: string) => uuid],
    (permissionUserDir, user_uuid): CallingUserPermission => ({
        listen: permissionUserDir.listen.has(user_uuid),
        whisper: permissionUserDir.whisper.has(user_uuid),
        barge: permissionUserDir.barge.has(user_uuid),
        pickup: permissionUserDir.pickup.has(user_uuid)
    })
);

const selectCallingShortcodes = createSelector(
    [selectShortCodes, selectCurrentUser],
    (
        shortCodes,
        user
    ): {
        callShortCodes: CallShortCodes;
        userShorCodePermissions: UserHasPermission;
    } => {
        const base: CallShortCodes = {
            listen: undefined,
            whisper: undefined,
            barge: undefined,
            pickup: undefined
        };
        const userCanPermission: UserHasPermission = {
            listen: false,
            whisper: false,
            barge: false,
            pickup: false
        };

        if (!shortCodes || !user) {
            return {
                callShortCodes: base,
                userShorCodePermissions: userCanPermission
            };
        }

        for (let i = 0; i < shortCodes.length; i += 1) {
            const sc = shortCodes[i];
            switch (sc.feature.feature) {
                case 'listen':
                    userCanPermission.listen = user.can_listen;
                    base.listen = sc.short_code;
                    break;
                case 'whisper':
                    userCanPermission.whisper = user.can_whisper;
                    base.whisper = sc.short_code;
                    break;
                case 'barge':
                    userCanPermission.barge = user.can_barge;
                    base.barge = sc.short_code;
                    break;
                case 'pickup':
                    userCanPermission.pickup = user.can_pickup;
                    base.pickup = sc.short_code;
                    break;
                default:
            }
        }

        return {
            callShortCodes: base,
            userShorCodePermissions: userCanPermission
        };
    }
);

export const selectCallingShortcodesOnUser = createSelector(
    [selectCallingShortcodes, selectAuthUserByUuid, selectCallingPermissionOnUser],
    ({ callShortCodes, userShorCodePermissions }, user, userCallingPermissions): CallShortCodes => {

        if (!user) {
            return {
                listen: undefined,
                whisper: undefined,
                barge: undefined,
                pickup: undefined
            };
        }

        const userSpecific: UserHasPermission = {
            pickup: user.can_be_picked_up,
            whisper: user.can_be_whispered,
            listen: user.can_be_listened,
            barge: user.can_be_barged
        };

        const getPermissionShotCode = (perm: SipPermissionFeature): string | undefined => {
            const code = callShortCodes[perm];

            if (!code) {
                return undefined;
            }

            if (
                userCallingPermissions[perm] ||
                (userShorCodePermissions[perm] && userSpecific[perm])
            ) {
                return code;
            }

            return undefined;
        };

        return {
            listen: getPermissionShotCode('listen'),
            whisper: getPermissionShotCode('whisper'),
            barge: getPermissionShotCode('barge'),
            pickup: getPermissionShotCode('pickup')
        }
    }
);

export const selectUserOrClient = createSelector(
    [
        selectUsersDictionary || (() => undefined),
        selectTenantClients || (() => undefined),
        (_, user_uuid: string) => user_uuid
    ],
    (users, tenantUsers, user_uuid): CallWhenAvailableUser | undefined => {
        const sip_user = users[user_uuid];

        if (sip_user) {
            return {
                user_uuid: sip_user.uuid,
                sip_name: sip_user.name,
                display_name: sip_user.nickname,
                extension: sip_user.extension.toString(),
                has_mailbox: !!sip_user.personal_mailbox,
            };
        }

        const client = tenantUsers[user_uuid];

        if (client) {
            return {
                user_uuid: client.user_uuid,
                sip_name: client.user_sip_name,
                display_name: client.user_display_name,
                extension: `${client.prefix}${client.extension}`,
                has_mailbox: false,
            };
        }

        return undefined;
    }
);

export const selectDnd = createSelector(
    [
        (state: RootState) => state,
        selectCurrentUserSipName,
        (state: RootState) => selectPhoneSettingByKey(state, 'dnd')
    ],
    (state: RootState, sipName: string, localDnd: boolean) => {
        const globalDnd = selectUserGlobalDnd(state, sipName);

        return {
            localDnd,
            globalDnd,
            dnd: localDnd || globalDnd
        };
    }
);

export const selectCurrentUserQueueAvailability = createSelector(
    [selectUserStatuses, selectCurrentUserSipName],
    (statuses, sipName) => {
        if (!sipName) return false;

        return statuses[sipName.toLowerCase()]?.queue_available;
    }
);

export const selectParkedCallsCount = createSelector(
    [selectUserStatuses, selectParkingSlots],
    (userStatuses: IUserStatus[], parkingSlots?: IParkingSlot[]): number | undefined => {
        if (!parkingSlots || !userStatuses) return undefined;

        let count = 0;

        for (let i = 0; i < parkingSlots.length; i += 1) {
            const status = `${parkingSlots[i].slot_number}@parking`;

            const foundStatus = userStatuses[status];

            if (foundStatus?.status === 'confirmed') {
                count += 1;
            }
        }

        return count;
    }
);

export const selectChatList = createSelector(
    [selectChatsOnly, selectFavouriteChats, selectMarkUnreadChats],
    (
        chats: IChat[],
        favouriteChats: Record<string, boolean>,
        markUnreadChats: Record<string, boolean>
    ): IChat[] =>
        chats.sort(
            (a, b) =>
                +(favouriteChats?.[b.uuid] || false) - +(favouriteChats?.[a.uuid] || false) ||
                +(markUnreadChats?.[b.uuid] || false) - +(markUnreadChats?.[a.uuid] || false) ||
                moment(b.latestMessage.time).unix() - moment(a.latestMessage.time).unix()
        )
);

export const selectThreadsList = createSelector(
    [selectAllChatEntries],
    (allChats: IChat[]): IChat[] =>
        allChats
            .filter(chat => {
                switch (true) {
                    case chat.type !== 'thread':
                        return false;
                    case !allChats.find(singleChat => singleChat.uuid === chat.source?.source_uuid):
                        return false;
                    default:
                        return true;
                }
            })
            .sort(
                (a, b) => moment(b.latestMessage.time).unix() - moment(a.latestMessage.time).unix()
            )
);

// export const selectAllUnreadThreads = createSelector(
//     [selectThreadsList, selectMutedChats, (state: RootState): string => state.user.uuid],
//     (threadList: IThread[], mutedChats: Record<string, boolean>, userUuid: string): number => {
//         if (!threadList || threadList.length < 1) return 0;
//
//         let total = 0;
//
//         const getParentId = (thread: IThread) => {
//             if (!thread) return undefined;
//
//             return thread.thread_message?.to === userUuid
//                 ? thread.thread_message?.from
//                 : thread.thread_message?.to;
//         };
//
//         for (let i = 0; i < threadList.length; i += 1) {
//             const parentId = threadList[i] && getParentId(threadList[i]);
//
//             if (parentId && threadList[i].unread_count && !mutedChats?.[parentId]) {
//                 total += 1;
//             }
//         }
//
//         return total;
//     }
// );

// export const selectAllUnreadChats = createSelector(
//     [selectChatList, selectMutedChats, selectAllUnreadThreads],
//     (chatList: IChat[], mutedChats: Record<string, boolean>, threadUnreadCount: number): number => {
//         if (!chatList || chatList.length < 1) return threadUnreadCount;
//
//         let total = 0;
//
//         for (let i = 0; i < chatList.length; i += 1) {
//             // if not muted and either has unread messages or it is marked as unread
//             if (!mutedChats?.[chatList[i].uuid] && chatList[i].unread_count !== 0) {
//                 total += 1;
//             }
//         }
//
//         return total;
//     }
// );

export const selectUnreadThreadMessages = createSelector(
    [selectThreadsList, selectMutedChats, (state: RootState): string => state.user.uuid],
    (threadList: IThread[], mutedChats: Record<string, boolean>, userUuid: string): number => {
        if (!threadList || threadList.length < 1) return 0;

        let total = 0;

        const getParentId = (thread: IThread) => {
            if (!thread) return undefined;

            return thread.thread_message?.to === userUuid
                ? thread.thread_message?.from
                : thread.thread_message?.to;
        };

        for (let i = 0; i < threadList.length; i += 1) {
            const parentId = threadList[i] && getParentId(threadList[i]);

            if (parentId && !mutedChats?.[parentId]) {
                const unreadCount = threadList[i].unread_count;

                total += unreadCount < 0 ? unreadCount * -1 : unreadCount;
            }
        }

        return total;
    }
);

export const selectAllChatUnreadMessages = createSelector(
    [selectChatList, selectMutedChats, selectUnreadThreadMessages],
    (chatList: IChat[], mutedChats: Record<string, boolean>, threadUnreadCount: number): number => {
        if (!chatList || chatList.length < 1) return threadUnreadCount;

        let total = 0;

        for (let i = 0; i < chatList.length; i += 1) {
            if (!mutedChats?.[chatList[i].uuid]) {
                const unreadCount = chatList[i].unread_count;

                total += unreadCount < 0 ? unreadCount * -1 : unreadCount;
            }
        }

        return total + threadUnreadCount;
    }
);

export const selectSipCredentials = createSelector(
    [selectAuthLoading, selectCurrentUserSipName, selectCurrentUserSipPassword, selectIsAus],
    (_, username, password, isAus): SIPLogin | null => {
        if (!username || !password) return null;

        return {
            username,
            password,
            isAus
        };
    }
);


type CallerDisplayNameGenericResponse = (
    | {
        type: 'user';
        data: UserEntity;
    }
    | {
        type: 'contact';
        data: PhonebookContactEntity;
    }
    | {
        type: 'callerId';
        data: undefined;
    }
    | {
        type: 'short_code';
        data: IShortcode;
    }
    | {
        type: 'unknown';
        data: undefined;
    }
    | {
        type: 'mailbox';
        data: IMailboxMenu;
    }
    | {
        type: undefined;
        data: undefined;
    }
) & {
    name: string;
};

export const selectCallerDisplayNameGeneric = createSelector(
    [
        selectUserCountryCode,
        selectAllCallerIds,
        selectShortCodes,
        (state: RootState, callerNumber: string | undefined) =>
            selectPhonebookEntityInArr(state, getNumbersArr(callerNumber || '', selectUserCountryCode(state))),
        (state: RootState, callerNumber: string | undefined) =>
            selectUserEntityByExtension(state, callerNumber),
        (state: RootState, callerNumber: string | undefined) => selectMailboxFromExt(state, callerNumber || ''),
        (_: RootState, callerNumber: string | undefined) => callerNumber
    ],
    (
        countryCode,
        callerIds,
        shortcodes,
        entity,
        userEntity,
        mailboxMenu,
        callerNumber
    ): CallerDisplayNameGenericResponse => {
        if (mailboxMenu) {
            return {
                type: 'mailbox',
                name: mailboxMenu.name,
                data: mailboxMenu
            };
        }

        if (!callerNumber) {
            return {
                type: 'unknown',
                data: undefined,
                name: 'unknown'
            };
        }

        if (entity && isValidContactEntity(entity)) {
            return {
                type: 'contact',
                data: entity,
                name: entity.name || callerNumber
            };
        }

        if (userEntity) {
            return {
                type: 'user',
                data: userEntity,
                name: userEntity.name
            };
        }

        const foundCallerId = callerIds?.find(cid => cid.cli_display === callerNumber);

        if (foundCallerId) {
            return {
                type: 'callerId',
                data: undefined,
                name: foundCallerId.cli_name
            };
        }

        const foundShortcode = shortcodes?.find(c => c.short_code === callerNumber.substring(0, 3));

        if (foundShortcode) {
            return {
                type: 'short_code',
                data: foundShortcode,
                name: callerNumber
            };
        }

        return {
            type: undefined,
            data: undefined,
            name: callerNumber
        };
    }
);

export interface SearchableConvo {
    seachVal: string,
    metaBloom: number,
    convo: Conversation,
}

const selectConvoSearchable = createSelector([
    selectNebulaChats,
    selectUsersDictionary,
    selectExtraChatters,
    selectMarkUnreadChats
],
    (convos, account_users, chatters, unreadChats): SearchableConvo[] =>
        convos.map(convo => {

            let seachVal: string;

            switch (convo.type) {
                case 'user':
                    seachVal = account_users[convo.uuid] ? `${account_users[convo.uuid]?.nickname} ${account_users[convo.uuid]?.extension}` : chatters[convo.uuid]?.name || "Unknown User";
                    break;
                case 'thread':
                    seachVal = "TEMP thread";
                    break;
                case 'integration':
                    seachVal = convo.integration?.name || convo.display_name;
                    break;
                default:
                    seachVal = convo.display_name;
            }

            let metaBloom = 0;

            if (convo.unread > 0 || unreadChats[convo.uuid]) {
                metaBloom |= 1;
            } else {
                metaBloom |= (1 << 1);
            }

            if (convo.is_favourite) {
                metaBloom |= (1 << 2);
            }
            if (convo.is_muted) {
                metaBloom |= (1 << 3);
            }
            if (convo.is_archived) {
                metaBloom |= (1 << 4);
            }

            switch (convo.type) {
                case 'integration':
                    metaBloom |= (1 << 5);
                    break;
                case 'user':
                    metaBloom |= (1 << 6);
                    break;
                case 'channel':
                    metaBloom |= (1 << 7);
                    break;
                case 'thread': metaBloom |= (1 << 8);
                    break;
            }

            if (convo.type !== 'thread') {
                metaBloom |= (1 << 9);
            }

            return {
                seachVal,
                metaBloom,
                convo
            }
        })
);

export const selectSearchConvos = createSelector([
    selectConvoSearchable,
    (_, searchVal: string, filters: string[], is_threads: boolean) => ({ searchVal, filters, is_threads }),
], (convos, { searchVal, filters, is_threads },): SearchableConvo[] => {
    let filter = 0;

    filters.forEach(f => {
        switch (f) {
            case 'Unread':
                filter |= 1;
                break;
            case 'Read':
                filter |= (1 << 1);
                break;
            case 'Pinned':
                filter |= (1 << 2);
                break;
            case 'Muted':
                filter |= (1 << 3);
                break;
            case 'Archived':
                filter |= (1 << 4);
                break;
            case 'SMS':
                filter |= (1 << 5);
                break;
            case 'Individual':
                filter |= (1 << 6);
                break;
            case 'Groups':
                filter |= (1 << 7);
                break;
        }
    });

    if (is_threads) {
        filter |= (1 << 8);
    } else {
        filter |= (1 << 9);
    }

    const cleanSearch = sanitizeStringForRegex(searchVal);

    if (!cleanSearch && filter === 0) {
        return convos;
    }

    const searchExp = cleanSearch ? new RegExp(cleanSearch, 'i') : null;

    return convos.filter(c => {
        if (filter !== 0 && ((c.metaBloom & filter) !== filter)) {
            return false;
        }
        if (searchExp) {
            return searchExp.test(c.seachVal);
        }
        return true;
    });
});

const selectNoChatUsers = createSelector([
    (state: { chatN: NebulaChatState }) => nebulaChatEntitySelectors.selectIds(state.chatN.chats),
    selectAllUsers,
], (chatIds, users) => {
    const chatIdSet = new Set(chatIds);
    return (users || []).filter(user => !chatIdSet.has(user.uuid));
})

export const selectSearchNoChatUsers = createSelector([
    selectNoChatUsers,
    (_state: any, searchVal?: string) => searchVal,
], (noChatUsers, searchVal): IUser[] => {
    if (!noChatUsers || !searchVal) return [];

    const cleanSearch = sanitizeStringForRegex(searchVal);

    if (!cleanSearch) {
        return [];
    }

    const searchExp = new RegExp(cleanSearch, 'i');

    return noChatUsers
        .filter(u => searchExp.test(u.nickname) || searchExp.test(String(u.extension)))
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        .toSorted((a, b) => a.nickname.localeCompare(b.nickname));
})

export const selectChatterByUuid = createSelector(
    [
        selectUsersDictionary,
        selectExtraChatters,
        (_state: RootState, userUuid: string | undefined) => userUuid,
    ],
    (users, extraChatters, userUuid): Chatter | undefined => {
        if (!userUuid) return undefined;

        if (userUuid === '__system__') {
            return {
                uuid: userUuid,
                name: "System",
                is_active: true,
                file_key: null,
                sip_name: undefined,
            }
        }

        const chatterBase = extraChatters[userUuid];
        if (chatterBase) {
            return chatterBase.is_active
                ? chatterBase
                : { ...chatterBase, name: `${chatterBase.name} (deleted)` };
        }

        const user = users[userUuid];
        if (user) {
            return {
                uuid: user.uuid,
                name: user.nickname,
                is_active: true,
                file_key: user.avatar_key,
                sip_name: user.name,
            };
        }

        return undefined;
    }
);
